import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import StructuredRecipeDisplay from '../components/StructuredRecipeDisplay';
import api from '../api/api';
import '../styles/tsumugi-design.css';

/**
 * FlyerRecipeResultPage - チラシから認識した特売品でレシピ表示
 * つむぎデザインシステム準拠
 *
 * フロー:
 * 1. localStorageから生成済みレシピを取得（flyerRecipeData）
 * 2. 結果を表示
 * 3. 自動保存
 */
const FlyerRecipeResultPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]); // 複数レシピ対応
  const [activeRecipeIndex, setActiveRecipeIndex] = useState(0); // 現在表示中のレシピインデックス
  const [isSaving, setIsSaving] = useState(false);
  const [saveCompleted, setSaveCompleted] = useState(false);

  // ページタイトル設定
  useEffect(() => {
    document.title = 'チラシレシピ結果 | つむぎ';
  }, []);

  /**
   * 初期化: localStorageからレシピデータ取得
   */
  useEffect(() => {
    const flyerRecipeDataStr = localStorage.getItem('flyerRecipeData');

    if (!flyerRecipeDataStr) {
      // データがない場合は元のページに戻る
      navigate('/flyer-recipe');
      return;
    }

    try {
      const parsedData = JSON.parse(flyerRecipeDataStr);

      // 複数レシピ対応: recipesがあればそれを使用、なければrecipeを配列に
      const recipeList = parsedData.recipes || (parsedData.recipe ? [parsedData.recipe] : []);

      if (recipeList.length === 0) {
        navigate('/flyer-recipe');
        return;
      }

      setIngredients(parsedData.ingredients || []);
      setRecipes(recipeList);

      // 全レシピを自動保存
      recipeList.forEach((recipe) => {
        saveGeneratedRecipe(recipe);
      });
    } catch (err) {
      console.error('Failed to parse flyer recipe data:', err);
      navigate('/flyer-recipe');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  /**
   * レシピを自動保存
   */
  const saveGeneratedRecipe = async (recipeMarkdown) => {
    if (!user) return; // 未ログインの場合は保存しない

    setIsSaving(true);
    try {
      // タイトルを抽出（## 料理名 の形式）
      const titleMatch = recipeMarkdown.match(/##\s+(.+)/);
      const title = titleMatch ? titleMatch[1].trim() : '特売品レシピ';

      // タグ
      const autoTags = ['特売品レシピ'];
      const lowerRecipe = recipeMarkdown.toLowerCase();
      if (lowerRecipe.includes('簡単') || lowerRecipe.includes('時短')) autoTags.push('簡単');

      // 保存API呼び出し
      await api.post('/api/recipes/save', {
        title,
        content: recipeMarkdown.trim(),
        sourceConsultationId: null,
        tags: autoTags
      });

      console.log('レシピを保存しました:', title);
      setSaveCompleted(true);
    } catch (error) {
      console.error('レシピ保存エラー:', error);
      // 保存失敗してもエラー表示はしない（ユーザー体験を損ねない）
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * 別のチラシを読み込む
   */
  const handleStartOver = () => {
    localStorage.removeItem('flyerRecipeData');
    navigate('/flyer-recipe');
  };

  /**
   * 保存済みレシピを見る
   */
  const handleViewSavedRecipes = () => {
    navigate('/recipes');
  };

  return (
    <AppLayout>
      <PageTitle
        title="特売品レシピ"
        subtitle="チラシの特売品から作れるレシピです"
        icon="🛒"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      <div className="px-3 py-2">
        {/* レシピ表示 */}
        {recipes.length > 0 && (
          <>
            {/* 使用した特売品 */}
            {ingredients.length > 0 && (
              <div className="tsumugi-card tsumugi-card--mint mb-3">
                <h3 className="tsumugi-card-title mb-2">🏷️ 使用した特売品</h3>
                <div className="flex flex-wrap gap-2">
                  {ingredients.map((ingredient, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-1 bg-white border border-mint rounded-full text-sm font-semibold text-gray-700"
                    >
                      {ingredient}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 複数レシピの場合はタブUIを表示 */}
            {recipes.length > 1 && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-gray-700">🍳 {recipes.length}つのレシピができました！</span>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {recipes.map((recipe, index) => {
                    // レシピタイトルを抽出
                    const titleMatch = recipe.match(/##\s+(.+)/);
                    const title = titleMatch ? titleMatch[1].trim() : `レシピ ${index + 1}`;
                    const isActive = activeRecipeIndex === index;
                    return (
                      <button
                        key={index}
                        onClick={() => setActiveRecipeIndex(index)}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                          isActive
                            ? 'bg-mint text-white shadow-md'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {title.length > 12 ? title.slice(0, 12) + '...' : title}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* レシピ本体 */}
            <div className="mb-3">
              <StructuredRecipeDisplay recipeMarkdown={recipes[activeRecipeIndex]} />
            </div>

            {/* 複数レシピの場合はナビゲーションボタンを表示 */}
            {recipes.length > 1 && (
              <div className="flex justify-between items-center mb-3 px-2">
                <button
                  onClick={() => setActiveRecipeIndex(Math.max(0, activeRecipeIndex - 1))}
                  disabled={activeRecipeIndex === 0}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeRecipeIndex === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-mint hover:bg-mint/10'
                  }`}
                >
                  ← 前のレシピ
                </button>
                <span className="text-sm text-gray-500">
                  {activeRecipeIndex + 1} / {recipes.length}
                </span>
                <button
                  onClick={() => setActiveRecipeIndex(Math.min(recipes.length - 1, activeRecipeIndex + 1))}
                  disabled={activeRecipeIndex === recipes.length - 1}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeRecipeIndex === recipes.length - 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-mint hover:bg-mint/10'
                  }`}
                >
                  次のレシピ →
                </button>
              </div>
            )}

            {/* アクションボタン */}
            <div className="flex gap-2 mb-3">
              <button
                className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--mint flex-1"
                onClick={handleStartOver}
              >
                🛒 別のチラシを読み込む
              </button>
              <button
                className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray flex-1"
                onClick={handleViewSavedRecipes}
              >
                📚 保存済みレシピを見る
              </button>
            </div>

            {/* ヒント */}
            <div className="tsumugi-card tsumugi-card--shell">
              <div className="flex items-start gap-2">
                <div className="text-xl">💡</div>
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    <strong>
                      {isSaving ? 'レシピを保存しています...' :
                       saveCompleted ? (recipes.length > 1 ? `${recipes.length}つのレシピを自動保存しました` : 'レシピは自動保存されました') :
                       user ? 'レシピを保存しています...' : 'ログインするとレシピを保存できます'}
                    </strong>
                  </p>
                  <p className="text-xs text-gray-600">
                    {user ? 'マイページの「保存したレシピ」からいつでも見返せます' :
                            'アカウントを作成して、レシピを保存しましょう'}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* データがない場合（通常は表示されない） */}
        {recipes.length === 0 && (
          <div className="tsumugi-card tsumugi-card--shell">
            <div className="flex items-start gap-4">
              <div className="text-4xl">📋</div>
              <div className="flex-1">
                <h3 className="tsumugi-card-title text-gray-700 mb-2">レシピがありません</h3>
                <p className="text-sm text-gray-600 mb-4">
                  チラシを読み込んでレシピを生成してください。
                </p>
                <button
                  className="tsumugi-btn tsumugi-btn-primary"
                  onClick={handleStartOver}
                >
                  チラシを読み込む
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default FlyerRecipeResultPage;
